import { useState } from "react";
import {
  DESIGNATION_LEVEL_OPTIONS,
  DESIGNATION_PAGE_SIZE_OPTIONS,
} from "../assets/instateamMasterConfig";
import {
  AppIcon,
  LabeledInput,
  MetricCard,
} from "../components/InstateamMasterComponents";

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

function getDesignationVisual(name) {
  const normalizedName = String(name || "").trim().toLowerCase();

  if (normalizedName.includes("software") || normalizedName.includes("developer")) {
    return { icon: "code", tone: "blue" };
  }

  if (normalizedName.includes("senior")) {
    return { icon: "shieldLock", tone: "green" };
  }

  if (normalizedName.includes("lead") || normalizedName.includes("manager")) {
    return { icon: "reports", tone: "violet" };
  }

  if (normalizedName.includes("marketing")) {
    return { icon: "announcements", tone: "amber" };
  }

  if (normalizedName.includes("hr")) {
    return { icon: "employees", tone: "gold" };
  }

  if (normalizedName.includes("account")) {
    return { icon: "payroll", tone: "red" };
  }

  if (normalizedName.includes("support")) {
    return { icon: "support", tone: "cyan" };
  }

  if (normalizedName.includes("office") || normalizedName.includes("admin")) {
    return { icon: "departments", tone: "slate" };
  }

  if (normalizedName.includes("design")) {
    return { icon: "palette", tone: "peach" };
  }

  return { icon: "designation", tone: "blue" };
}

function getDepartmentTone(departmentName) {
  const normalizedName = String(departmentName || "").trim().toLowerCase();

  if (normalizedName.includes("develop") || normalizedName.includes("engineer")) return "blue";
  if (normalizedName.includes("marketing")) return "orange";
  if (normalizedName.includes("hr")) return "violet";
  if (normalizedName.includes("finance")) return "red";
  if (normalizedName.includes("support")) return "cyan";
  if (normalizedName.includes("admin")) return "slate";
  if (normalizedName.includes("sales")) return "amber";
  if (normalizedName.includes("design")) return "peach";
  return "blue";
}

function DesignationFormModal({
  isOpen,
  form,
  onFormChange,
  onClose,
  onSubmit,
  isSaving,
  editingDesignation,
  departmentOptions,
}) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="department-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>{editingDesignation ? "Edit Designation" : "Add Designation"}</h3>
            <p>
              {editingDesignation
                ? "Update designation details, department, level, and active state."
                : "Create a designation that can be assigned directly from the employee form."}
            </p>
          </div>

          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <form className="department-modal-form" onSubmit={onSubmit}>
          <div className="department-modal-grid">
            <LabeledInput
              label="Designation Name"
              required
              value={form.name}
              onChange={(value) => onFormChange((current) => ({ ...current, name: value }))}
              placeholder="Enter designation name"
            />

            <label className="form-field">
              <span>
                Department <em>*</em>
              </span>
              <div className="field-shell select-shell">
                <select
                  value={form.department}
                  onChange={(event) =>
                    onFormChange((current) => ({
                      ...current,
                      department: event.target.value,
                    }))
                  }
                >
                  <option value="">Select department</option>
                  {departmentOptions.map((department) => (
                    <option key={department} value={department}>
                      {department}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field">
              <span>
                Level
              </span>
              <div className="field-shell select-shell">
                <select
                  value={form.level}
                  onChange={(event) =>
                    onFormChange((current) => ({ ...current, level: event.target.value }))
                  }
                >
                  {DESIGNATION_LEVEL_OPTIONS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field">
              <span>
                Status
              </span>
              <div className="field-shell select-shell">
                <select
                  value={form.status}
                  onChange={(event) =>
                    onFormChange((current) => ({ ...current, status: event.target.value }))
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field department-form-wide">
              <span>
                Description <em>*</em>
              </span>
              <div className="textarea-shell">
                <textarea
                  value={form.description}
                  onChange={(event) =>
                    onFormChange((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  placeholder="Enter designation description"
                  rows={5}
                />
              </div>
            </label>
          </div>

          <div className="form-footer">
            <span className="required-note">* Marked fields are required</span>
            <div className="form-footer-actions">
              <button className="secondary-button" type="button" onClick={onClose}>
                Cancel
              </button>
              <button className="primary-button save-button" type="submit">
                {isSaving
                  ? "Saving..."
                  : editingDesignation
                    ? "Save Changes"
                    : "Save Designation"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DesignationsPage({
  designations,
  metrics,
  searchText,
  onSearchChange,
  departmentFilter,
  departmentOptions,
  onDepartmentFilterChange,
  statusFilter,
  onStatusFilterChange,
  onClearFilters,
  onAddDesignation,
  onEditDesignation,
  onToggleDesignationStatus,
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  resultsCount,
  isLoadingDesignations,
  isDesignationModalOpen,
  designationForm,
  onDesignationFormChange,
  onCloseDesignationModal,
  onSubmitDesignation,
  isSavingDesignation,
  editingDesignation,
}) {
  const [openActionMenuId, setOpenActionMenuId] = useState(null);

  return (
    <section className="designations-page">
      <div className="metrics-grid">
        <MetricCard
          label="Total Designations"
          value={metrics.totalDesignations}
          helper="All designations"
          icon="designation"
        />
        <MetricCard
          label="Active Designations"
          value={metrics.activeDesignations}
          helper="Currently active"
          icon="activeUsers"
          tone="success"
        />
        <MetricCard
          label="Inactive Designations"
          value={metrics.inactiveDesignations}
          helper="Currently inactive"
          icon="inactiveUsers"
          tone="danger"
        />
        <MetricCard
          label="Employees in Designations"
          value={metrics.employeesInDesignations}
          helper="Across all designations"
          icon="employees"
        />
      </div>

      <div className="table-card designation-table-card">
        <div className="department-page-toolbar">
          <div className="department-page-copy">
            <strong>Designation Directory</strong>
            <span>Manage job titles, levels, departments, descriptions, and designation status.</span>
          </div>

          <button className="primary-button add-button" type="button" onClick={onAddDesignation}>
            <AppIcon name="plus" />
            Add Designation
          </button>
        </div>

        <div className="designation-filter-row">
          <label className="inline-search-field">
            <AppIcon name="search" />
            <input
              value={searchText}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search designations..."
            />
          </label>

          <div className="designation-filter-actions">
            <label className="inline-select-shell">
              <select
                value={departmentFilter}
                onChange={(event) => onDepartmentFilterChange(event.target.value)}
              >
                <option value="all">All Departments</option>
                {departmentOptions.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </label>

            <label className="inline-select-shell">
              <select
                value={statusFilter}
                onChange={(event) => onStatusFilterChange(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </label>

            <button className="secondary-icon-button" type="button" onClick={onClearFilters}>
              <AppIcon name="filter" />
            </button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="designation-table">
            <thead>
              <tr>
                <th>Designation</th>
                <th>Department</th>
                <th>Level</th>
                <th>Employees</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoadingDesignations ? (
                <tr>
                  <td colSpan="7" className="empty-table-state">
                    Loading designations...
                  </td>
                </tr>
              ) : null}

              {!isLoadingDesignations && !designations.length ? (
                <tr>
                  <td colSpan="7" className="empty-table-state">
                    No designations found. Add the first designation to get started.
                  </td>
                </tr>
              ) : null}

              {!isLoadingDesignations
                ? designations.map((designation) => {
                    const visual = getDesignationVisual(designation.name);
                    const departmentTone = getDepartmentTone(designation.department);

                    return (
                      <tr key={designation._id}>
                        <td>
                          <div className="designation-name-cell">
                            <div className={`department-icon-chip ${visual.tone}`}>
                              <AppIcon name={visual.icon} />
                            </div>

                            <div className="department-name-copy">
                              <strong>{designation.name}</strong>
                              <span>{designation.level || "Designation"}</span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className={`department-tag ${departmentTone}`}>
                            {designation.department}
                          </span>
                        </td>

                        <td>
                          <span className={`level-badge ${designation.level?.toLowerCase() || "l1"}`}>
                            {designation.level}
                          </span>
                        </td>

                        <td>{designation.employeeCount}</td>
                        <td className="designation-description-cell">
                          {designation.description || "No description added yet."}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              designation.status === "active" ? "active" : "inactive"
                            }`}
                          >
                            {designation.status === "active" ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="row-actions">
                            <button
                              className="action-icon-button"
                              type="button"
                              onClick={() => onEditDesignation(designation)}
                            >
                              <AppIcon name="edit" />
                            </button>

                            <div className="row-action-shell">
                              <button
                                className="action-icon-button"
                                type="button"
                                onClick={() =>
                                  setOpenActionMenuId((current) =>
                                    current === designation._id ? null : designation._id
                                  )
                                }
                              >
                                <AppIcon name="more" />
                              </button>

                              {openActionMenuId === designation._id ? (
                                <div className="row-action-menu">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenActionMenuId(null);
                                      onToggleDesignationStatus(designation);
                                    }}
                                  >
                                    {designation.status === "active"
                                      ? "Mark Inactive"
                                      : "Mark Active"}
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                : null}
            </tbody>
          </table>
        </div>

        <div className="table-footer department-table-footer">
          <span>
            Showing {resultsCount ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, resultsCount)} of {resultsCount} designations
          </span>

          <div className="department-footer-actions">
            <div className="pagination">
              <button
                className="pagination-arrow"
                type="button"
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                <AppIcon name="chevronLeft" />
              </button>

              {Array.from({ length: totalPages }, (_, index) => index + 1)
                .slice(0, Math.min(totalPages, 5))
                .map((page) => (
                  <button
                    key={page}
                    className={`page-chip ${page === currentPage ? "active" : ""}`}
                    type="button"
                    onClick={() => onPageChange(page)}
                  >
                    {page}
                  </button>
                ))}

              <button
                className="pagination-arrow"
                type="button"
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                <AppIcon name="chevronRight" />
              </button>
            </div>

            <label className="page-size-shell">
              <select
                value={pageSize}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
              >
                {DESIGNATION_PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </label>
          </div>
        </div>
      </div>

      <DesignationFormModal
        isOpen={isDesignationModalOpen}
        form={designationForm}
        onFormChange={onDesignationFormChange}
        onClose={onCloseDesignationModal}
        onSubmit={onSubmitDesignation}
        isSaving={isSavingDesignation}
        editingDesignation={editingDesignation}
        departmentOptions={departmentOptions}
      />
    </section>
  );
}
